import React, { useCallback, useMemo, useState } from 'react'

import { Button, Skeleton } from 'antd'
import { HStack, Icon, T, VStack } from 'govwell-ui'

import { navigate, routes } from '@redwoodjs/router'

import GeneralLedgerBreakdown from 'src/components/Settlement/GeneralLedgerBreakdown'
import SettlementTransactionsTable from 'src/components/Settlement/SettlementTransactionsTable'
import SummarySection from 'src/components/Settlement/SummarySection'
import Page from 'src/components/shared/Page/Page'
import {
  useGenerateSettlementReportMutation,
  useGetSettlementByUuidQuery,
} from 'src/fetch/settlements'
import useDisclosure from 'src/hooks/use-disclosure'
import ShareSettlementModal from 'src/pages/SettlementsPage/ShareSettlementModal/ShareSettlementModal'
import { downloadFile } from 'src/utils/download'
import { monitoring, MonitoringContextKey, MonitoringEvent } from 'src/utils/monitoring'

type Props = {
  settlementUuid: string
}

const SettlementPage = (props: Props) => {
  const { settlementUuid } = props

  const { settlement, isLoading, error } = useGetSettlementByUuidQuery(settlementUuid)

  const accrualWindow = settlement?.accrualWindowDisplay || ''
  const estimatedDepositDate = settlement?.estimatedBankDepositDate || null
  const totalCents = settlement?.netAmountCents ?? null
  const approvedAt = settlement?.finixSettlementApprovedAt || null
  const timeZone = settlement?.organization?.timeZone || null

  const ledgerAllocations = useMemo(() => {
    return settlement?.revenueAllocationByGeneralLedgerNumber ?? []
  }, [settlement?.revenueAllocationByGeneralLedgerNumber])

  const generalLedgerNumbers = useMemo(() => {
    return settlement?.generalLedgerNumbers ?? []
  }, [settlement?.generalLedgerNumbers])

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back()
      return
    }
    navigate(routes.settlements())
  }, [])

  const title = settlement?.finixSettlementId
    ? `Settlement Details - ${settlement.finixSettlementId}`
    : 'Settlement Details'

  const [isGeneratingSettlementReport, setIsGeneratingSettlementReport] = useState(false)
  const modalState = useDisclosure()

  const { mutateAsync: generateSettlementReport } = useGenerateSettlementReportMutation()

  const handleGenerateSettlementReport = useCallback(async () => {
    if (settlement?.id) {
      monitoring.captureEvent(MonitoringEvent.SettlementReportDownloaded, {
        [MonitoringContextKey.SettlementId]: settlement?.id,
      })
    }
    try {
      setIsGeneratingSettlementReport(true)
      const res = await generateSettlementReport({ settlementId: settlement?.id ?? 0 })
      const file = res.generateSettlementReport
      await downloadFile({
        url: file.url?.signedUrl ?? '',
        downloadableName: file.downloadableName ?? '',
      })
    } finally {
      setIsGeneratingSettlementReport(false)
    }
  }, [generateSettlementReport, settlement?.id])

  const headerActions = useMemo(() => {
    if (!settlement) {
      return null
    }
    return (
      <HStack gap={2} justify="end">
        <Button icon={<Icon.FaShareNodes />} onClick={modalState.open} disabled={!settlement}>
          Share
        </Button>
        <Button
          type="primary"
          icon={<Icon.FaFileArrowDown />}
          onClick={handleGenerateSettlementReport}
          loading={isGeneratingSettlementReport}
        >
          Download PDF
        </Button>
      </HStack>
    )
  }, [handleGenerateSettlementReport, isGeneratingSettlementReport, modalState.open, settlement])

  return (
    <Page>
      <Page.Header
        title={title}
        customTitleDisplay={
          <HStack justify="space-between">
            <HStack gap={4} align="center">
              <Button type="default" icon={<Icon.FaChevronLeft />} onClick={handleBack}>
                Back
              </Button>
              <HStack gap={3} align="center">
                <T.H1>{title}</T.H1>
              </HStack>
            </HStack>
          </HStack>
        }
        actions={headerActions}
      />
      <Page.Content>
        {isLoading ? (
          <Skeleton active />
        ) : error ? (
          <T.Body>We were unable to load this settlement. Please try again later.</T.Body>
        ) : !settlement ? (
          <T.Body>No settlement data is available.</T.Body>
        ) : (
          <VStack gap={6} align="stretch">
            <SummarySection
              accrualWindow={accrualWindow}
              estimatedDepositDate={estimatedDepositDate}
              totalCents={totalCents}
              timeZone={timeZone}
              approvedAt={approvedAt}
            />
            <GeneralLedgerBreakdown
              allocations={ledgerAllocations}
              generalLedgerNumbers={generalLedgerNumbers}
              totalCents={totalCents ?? 0}
              timeZone={timeZone}
            />
            <SettlementTransactionsTable settlement={settlement} />
          </VStack>
        )}
      </Page.Content>
      {modalState.isOpen && settlement && (
        <ShareSettlementModal
          finixSettlementId={settlement.finixSettlementId}
          onClose={modalState.close}
          settlementId={settlement.id}
          accrualWindow={accrualWindow}
        />
      )}
    </Page>
  )
}

export default React.memo(SettlementPage)
